import {Button, DatePicker, Form} from 'antd';
import classNames from 'classnames';
import isNil from 'lodash/isNil';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {Fragment, useCallback, useMemo, useState} from 'react';

import FilterContent from '@/components/TableFilters/components/FilterContent';
import {
  getLastSevenDays,
  getThisMonth,
  getThisWeek
} from '@/components/TableFilters/components/Types/DateRangeComponent/utils';

import {getLastMonth, getLastThirtyDays, getRangeByDays, getThisYear, getTodayRange} from './utils';

const {RangePicker} = DatePicker;

export const moreDaysValues = [30, 90, 180, 365];

export const DateItemSelector = ({value, onChange, hideCustom, defaultIndex, className, hideOptions, t, ...rest}) => {
  const [activeIndex, setActiveIndex] = useState(defaultIndex || -1);

  const getIfIsActive = useCallback((index) => {
    if (activeIndex === index)
      return 'primary';
    return 'default';
  }, [activeIndex]);

  const onSelectFilter = useCallback((value, index) => {
    onChange(value);
    setActiveIndex(index);
  }, [onChange]);

  const checkIfActive = useCallback((index) => {
    return hideOptions?.some(e => e === index);
  }, [hideOptions]);

  return (<Fragment>
    <div className={classNames(className, 'flex flex-wrap md:p-1 justify-end gap-y-1')}>
      <Button className={classNames('mx-1 mb-1 md:mb-0 ml-0 px-2', {'hidden': checkIfActive(0)})}
        type={getIfIsActive(0)}
        onClick={() => onSelectFilter(getTodayRange(), 0)}>
        {t('dateFilter.today')}
      </Button>
      <Button className={classNames('mx-1 mb-1 md:mb-0 ml-0 px-2', {'hidden': checkIfActive(1)})}
        type={getIfIsActive(1)}
        onClick={() => onSelectFilter(getThisWeek(), 1)}>
        {t('dateFilter.thisWeek')}
      </Button>
      <Button className={classNames('mx-1 mb-1 md:mb-0 ml-0 px-2', {'hidden': checkIfActive(2)})}
        type={getIfIsActive(2)}
        onClick={() => onSelectFilter(getLastSevenDays(), 2)}>
        {t('dateFilter.lastSevenDays')}
      </Button>
      <Button className={classNames('mx-1 mb-1 md:mb-0 ml-0 px-2', {'hidden': checkIfActive(3)})}
        type={getIfIsActive(3)}
        onClick={() => onSelectFilter(getThisMonth(), 3)}>
        {t('dateFilter.thisMonth')}
      </Button>
      <Button className={classNames('mx-1 mb-1 md:mb-0 ml-0 px-2', {'hidden': checkIfActive(4)})}
        type={getIfIsActive(4)}
        onClick={() => onSelectFilter(getLastThirtyDays(), 4)}>
        {t('dateFilter.lastThirtyDays')}
      </Button>
      <Button className={classNames('mx-1 mb-1 md:mb-0 ml-0 px-2', {'hidden': checkIfActive(5)})}
        type={getIfIsActive(5)}
        onClick={() => onSelectFilter(getLastMonth(), 5)}>
        {t('dateFilter.lastMonth')}
      </Button>
      <Button className={classNames('mx-1 mb-1 md:mb-0 ml-0 px-2', {'hidden': checkIfActive(6)})}
        type={getIfIsActive(6)}
        onClick={() => onSelectFilter(getThisYear(), 6)}>
        {t('dateFilter.thisYear')}
      </Button>
    </div>
    <hr className="border-gray-300"/>
    <div className={classNames(className, 'flex flex-wrap md:p-1 justify-end gap-y-1')}>
      {moreDaysValues?.map((days, index) => <Button
        key={index}
        className={classNames('mx-1 mb-1 md:mb-0 ml-0 px-2', {'hidden': checkIfActive(7 + index)})}
        type={getIfIsActive(0)}
        onClick={() => onSelectFilter(getRangeByDays(days), 7 + index)}>
        {t('dateFilter.more' + days + 'days')}
      </Button>)}
    </div>
    {!hideCustom && <div className="md:px-1 pl-0 mt-2">
      <RangePicker className="w-full" value={value} onChange={onChange} {...rest} />
    </div>}
  </Fragment>);
};

DateItemSelector.propTypes = {
  t: PropTypes.func,
  hideOptions: PropTypes.array,
  className: PropTypes.string,
  value: PropTypes.any,
  hideCustom: PropTypes.bool,
  defaultIndex: PropTypes.number,
  onChange: PropTypes.func
};

DateItemSelector.defaultProps = {
  hideOptions: []
};

export const DateRangeComponent = ({filter, t, applyFilter}) => {

  return (
    <div style={{maxWidth: '20rem'}}>
      <Form onFinish={({value}) => {
        if (value) {
          const [from, to] = value;
          //
          const query = {field: filter.field, type: 'RANGE'};
          if (from)
            query.from = from.startOf('date').toISOString();
          if (to)
            query.to = to.endOf('date').toISOString();

          applyFilter(query);
        }
      }}>
        <FilterContent t={t}>
          <Form.Item name="value" className="m-0">
            <DateItemSelector t={t}/>
          </Form.Item>
        </FilterContent>
      </Form>
    </div>
  );
};

DateRangeComponent.propTypes = {
  filter: PropTypes.object,
  t: PropTypes.func,
  applyFilter: PropTypes.func
};

export const ValueDateRange = ({filterValue}) => {
  return useMemo(() => {
    const {to, from} = filterValue;
    if (!isNil(from) && !isNil(to)) {
      return `${moment(from).format('LL').toString()} - ${moment(to).format('LL').toString()}`;
    }

    return null;
  }, [filterValue]);
};

ValueDateRange.propTypes = {
  filter: PropTypes.object,
  t: PropTypes.func,
  filterValue: PropTypes.any
};
